export interface PointSettings {
  markerType: "dot" | "icon" | "raw";
  svg?: string;
  color?: [number, number, number];
  size?: number;
  borderColor: [number, number, number];
  borderSize: number;
  opacity: number;
  heatmapType: "hex" | "cloud";
  heatmapPrimaryColor: [number, number, number];
  heatmapSecondaryColor: [number, number, number];
  heatmapStrokeColor: [number, number, number];
  heatmapStroke: number;
  heatmapOpacity: number;
  heatmapThreshold?: number;
  heatmapRadiusPixels?: number;
  heatmapIntensity?: number;
  heatmapCloudThreshold?: number;
}

export interface PolygonSettings {
  color: [number, number, number];
  borderColor: [number, number, number] | null;
  borderSize: number;
  opacity: number;
}

export interface DatapointConfig {
  label: string;
  field: string;
}

export interface DrivetimePolygonConfig {
  minRange: number;
  maxRange: number;
  step: number;
}

export interface DrivetimePolygonPreset {
  start: number;       // Start time in minutes
  stepSize: number;    // Step size in minutes
  steps: number;       // Number of steps
  showEvery: number;   // Show every n steps
}

export interface RadiusConfig {
  radius: number; // in miles
  steps: number;  // number of equal-distance buckets
}

export interface RadiusPreset {
  radius: number;      // Total radius in miles
  stepSize: number;    // Step size in miles
  showEvery: number;   // Show every n steps
}

export interface DrivetimeAggregationConfig {
  label: string;
  layerId: string;
  version: number;
  aggregationField?: string; // Optional - if not provided, just count matches
}

export type AggregationMode = 'drivetime' | 'radius';

export interface CentersViewConfig {
  titleField?: string;
  addressField?: string;
  datapoints: DatapointConfig[];
  drivetimePolygons?: DrivetimePolygonConfig; // Legacy single config (backwards compat)
  drivetimePresets?: DrivetimePolygonPreset[]; // List of named drivetime presets
  radiusConfig?: RadiusConfig; // Legacy single config (backwards compat)
  radiusPresets?: RadiusPreset[]; // List of named radius presets
  drivetimeAggregations: DrivetimeAggregationConfig[];
  aggregationMode?: AggregationMode; // Deprecated — kept for backwards compat with stored configs
}

export interface ViewSettings {
  type: "centers" | "pcp" | "ml";
  centersConfig?: CentersViewConfig;
}

// Color Code Data configuration - allows cross-layer color-coding by joining on a field
export interface ColorCodeConfig {
  sourceLayerId: string;       // The layer to use as the data source (e.g., Patients)
  joinField: string;           // Field on THIS layer to join on (e.g., center_id)
  sourceJoinField: string;     // Field on the SOURCE layer to join on (e.g., assigned_center_id)
  palette: [number, number, number][]; // Array of RGB colors in the palette
  // Runtime color assignments: maps a join field value to a palette index
  // This is persisted to maintain "sticky" color assignments
  colorAssignments?: Record<string, number>;
  // Opacity for points that don't match any visible join value (0-1, default 0.2)
  unmatchedOpacity?: number;
}

// Default 8-color palette: easily distinguishable on a map
export const DEFAULT_COLOR_CODE_PALETTE: [number, number, number][] = [
  [31, 119, 180],   // Blue
  [255, 127, 14],   // Orange
  [44, 160, 44],    // Green
  [214, 39, 40],    // Red
  [148, 103, 189],  // Purple
  [140, 86, 75],    // Brown
  [227, 119, 194],  // Pink
  [23, 190, 207],   // Teal
];

export interface SchemaField {
  type: 'bool' | 'integer' | 'float' | 'timestamp' | 'string' | 'geometry';
  isUnique: boolean;
}

export interface FieldSchema {
  [fieldName: string]: SchemaField | 'bool' | 'integer' | 'float' | 'timestamp' | 'string' | 'geometry';
}

// Layer configuration (combines metadata and settings)
export interface LayerConfig {
  id: string;
  type: 'points' | 'polygons';
  name: string;
  visible: boolean;
  pointSettings: PointSettings;
  polygonSettings?: PolygonSettings;
  viewSettings?: ViewSettings;
  colorCodeConfig?: ColorCodeConfig;
  count: number; // Total number of points/polygons
  schema: FieldSchema;
  last_updated: number; // Unix Timestamp
  version: number;
  status?: 'pending' | 'processed' | 'failed';
  error_message?: string;
}

// File-level settings (stored once per file, applies to all versions)
export interface FileSettings {
  id: string;
  type: 'points' | 'polygons';
  name: string; // Display name for the file (applies to all versions)
  pointSettings?: PointSettings;
  polygonSettings?: PolygonSettings;
  viewSettings?: ViewSettings;
  colorCodeConfig?: ColorCodeConfig;
  last_updated?: number;
}

export interface UploadRequest {
  metadata: {
    id?: string; // Optional for new files, required for new versions
    version?: number; // Required when uploading new version
    type: 'points' | 'polygons';
    name: string;
    pointSettings?: PointSettings;
    polygonSettings?: PolygonSettings;
    viewSettings?: ViewSettings;
  };
  fileName?: string;
}

export interface UpdateSettingsRequest {
  id: string;
  name?: string;
  pointSettings?: PointSettings | null;
  polygonSettings?: PolygonSettings | null;
  viewSettings?: ViewSettings | null;
  colorCodeConfig?: ColorCodeConfig | null;
}

export interface UploadResponse {
  uploadUrl: string;
  fileId: string;
  version: number;
  fileName: string;
}

export interface MetadataListResponse {
  count: number;
  files: LayerConfig[];
}

export interface DataPoint {
  coords: number[];
  weight: number | null;
  holes: number[][];
  h3Index?: string; // For H3 aggregation data
  recordCount?: number; // For H3 aggregation data
  id?: string | number; // Unique identifier for the record
  fields?: Record<string, any>; // Extra field data (e.g., for color code joins)
}

// Helper functions for working with schema
export function getSchemaFieldType(schemaEntry: SchemaField | string): string {
  return typeof schemaEntry === 'object' ? schemaEntry.type : schemaEntry;
}

export function isSchemaFieldUnique(schemaEntry: SchemaField | string): boolean {
  return typeof schemaEntry === 'object' && schemaEntry.isUnique === true;
}

export function getUniqueFields(schema: FieldSchema): string[] {
  const uniqueFields: string[] = [];
  for (const [fieldName, schemaEntry] of Object.entries(schema)) {
    if (isSchemaFieldUnique(schemaEntry)) {
      uniqueFields.push(fieldName);
    }
  }
  return uniqueFields;
}

export function getIdField(schema: FieldSchema): string | null {
  // Return the first unique field (for backwards compatibility)
  const uniqueFields = getUniqueFields(schema);
  return uniqueFields.length > 0 ? uniqueFields[0] : null;
}
